import React from 'react';
import { FileTextIcon, UsersIcon, CheckCircle2Icon, PhoneIcon, MessageSquareIcon, BellIcon, MailIcon, SparklesIcon, ArrowRightIcon, UserIcon, MapPinIcon } from 'lucide-react';
import { networkCopy, citizenNodes, CTA_CITY_MAP, CTA_SNOWPLOW, LOGO_BCITI, LOGO_ATNA, ctaCopy, type Bi } from '../../../data/communicationsFinalCta';
import { previewCopy } from '../../../data/communicationsProof';
type P = {
  isFR: boolean;
};
const pk = (isFR: boolean) => (v: Bi) => isFR ? v.fr : v.en;
const CARD = 'rounded-2xl border border-[#ECECF4] bg-white shadow-[0_18px_40px_-28px_rgba(40,36,120,0.45)]';
export function IntelligenceCard({
  isFR,
  kind
}: P & {
  kind: 'notice' | 'audience';
}) {
  const pick = pk(isFR);
  const c = networkCopy[kind];
  const Icon = kind === 'notice' ? FileTextIcon : UsersIcon;
  return <div className={`${CARD} px-4 py-3.5 flex items-start gap-3.5`} data-id="element-2585" data-mp="29lnrsyydxqt0">
      <span className="w-12 h-12 rounded-xl bg-[#F1F0FD] flex items-center justify-center shrink-0" aria-hidden="true" data-id="element-2586" data-mp="24ncicpc05ibb">
        <Icon className="w-6 h-6 text-[#4F46E5]" strokeWidth={1.6} data-id="element-2587" data-mp="cm2qj84yqbnn" />
      </span>
      <span className="min-w-0" data-id="element-2588" data-mp="hmqka1kkt6lk">
        <span className="flex items-center gap-2 font-sans text-[15px] font-semibold text-[#121A3A]" data-id="element-2589" data-mp="hns4avjkl3ff">
          {pick(c.title)}
          <CheckCircle2Icon className="w-[18px] h-[18px] text-white fill-[#22A04F] shrink-0" aria-hidden="true" data-id="element-2590" data-mp="2rawufei48y5" />
        </span>
        <span className="block font-sans text-[12.5px] text-[#5B6478] leading-snug mt-1" data-id="element-2591" data-mp="vd2ce0h5v5cp">{pick(c.body)}</span>
      </span>
    </div>;
}
const CH_ICONS = {
  voice: PhoneIcon,
  sms: MessageSquareIcon,
  push: BellIcon,
  email: MailIcon
} as const;
export function MultichannelCard({
  isFR
}: P) {
  const pick = pk(isFR);
  return <div className={`${CARD} p-3 flex items-center gap-3.5`} data-id="element-2592" data-mp="qlkmc7goep6i">
      <img src={CTA_SNOWPLOW} alt="" className="w-[78px] h-[78px] rounded-lg object-cover shrink-0" data-id="element-2593" data-mp="1ecpzufz595dn" />
      <div className="min-w-0" data-id="element-2594" data-mp="1j2wpmj5e6vxh">
        <p className="font-sans text-[14px] font-semibold text-[#121A3A]" data-id="element-2595" data-mp="1j3ybliny52tz">{pick(networkCopy.multichannel.title)}</p>
        <ul className="mt-2 flex gap-3" data-id="element-2596" data-mp="1syn5bbfg5svv">
          {networkCopy.multichannel.channels.map(ch => {
          const Icon = CH_ICONS[ch.key as keyof typeof CH_ICONS];
          return <li key={ch.key} className="flex flex-col items-center gap-1" data-id="element-2597" data-mp="1jwxarwq9qdtv">
                <span className="w-8 h-8 rounded-lg bg-[#F1F0FD] flex items-center justify-center" aria-hidden="true" data-id="element-2598" data-mp="1eycajxb0nu5f">
                  <Icon className="w-4 h-4 text-[#4F46E5]" strokeWidth={1.9} data-id="element-2599" data-mp="1a1vnl6fjzltx" />
                </span>
                <span className="font-sans text-[11px] text-[#3A4050]" data-id="element-2600" data-mp="15817se32jr04">{pick(ch.label)}</span>
              </li>;
        })}
        </ul>
      </div>
    </div>;
}
export function ReachCard({
  isFR
}: P) {
  const pick = pk(isFR);
  const r = networkCopy.reach;
  const C = 2 * Math.PI * 22;
  return <div className={`${CARD} px-4 py-4 flex items-center gap-4`} data-id="element-2601" data-mp="r91geyo68y09">
      <svg viewBox="0 0 56 56" className="w-[72px] h-[72px] -rotate-90 shrink-0" aria-hidden="true" data-id="element-2602" data-mp="mbrj6wujji1v">
        <circle cx="28" cy="28" r="22" fill="none" stroke="#ECEBFB" strokeWidth="6" data-id="element-2603" data-mp="2xx0i2xngwqr6" />
        <circle cx="28" cy="28" r="22" fill="none" stroke="#4F46E5" strokeWidth="6" strokeLinecap="round" strokeDasharray={`${C * 0.92} ${C}`} data-id="element-2604" data-mp="32wdf2b81ze7h" />
      </svg>
      <div data-id="element-2605" data-mp="37uew6qlx53gb">
        <p className="font-sans text-[26px] font-semibold text-[#121A3A] leading-none" data-id="element-2606" data-mp="3bxupbcys4lo9">{pick(r.value)}</p>
        <p className="font-sans text-[13px] text-[#5B6478] mt-1" data-id="element-2607" data-mp="2f7u57r9cutda">{pick(r.label)}</p>
        <p className="font-sans text-[15px] font-semibold text-[#1E8A45] mt-1.5 leading-none" data-id="element-2608" data-mp="2f5r68hq85pwf">{pick(r.trend)}</p>
        <p className="font-sans text-[11.5px] text-[#1E8A45] mt-0.5" data-id="element-2609" data-mp="2o8ikepahizrr">{pick(r.sub)}</p>
      </div>
    </div>;
}
export function AtnaRecommendation({
  isFR
}: P) {
  const pick = pk(isFR);
  return <div className={`${CARD} px-4 py-3 flex items-center gap-3.5`} data-id="element-2610" data-mp="1wh2rkdic4gt3">
      <span className="w-11 h-11 rounded-xl bg-[#F1F0FD] flex items-center justify-center shrink-0" aria-hidden="true" data-id="element-2611" data-mp="1wgud4h6kd3fs">
        <SparklesIcon className="w-5 h-5 text-[#4F46E5]" strokeWidth={1.7} data-id="element-2612" data-mp="1he2lh7j8b1r4" />
      </span>
      <span className="flex-1 min-w-0" data-id="element-2613" data-mp="1hbq714coj9it">
        <span className="block font-sans text-[14px] font-semibold text-[#121A3A]" data-id="element-2614" data-mp="1cfrcez0rcv9y">{pick(networkCopy.reco.title)}</span>
        <span className="block font-sans text-[12px] text-[#5B6478] leading-snug mt-0.5" data-id="element-2615" data-mp="1r4ns9i923cl4">{pick(networkCopy.reco.body)}</span>
      </span>
      <span className="w-8 h-8 rounded-full bg-[#F1F0FD] flex items-center justify-center shrink-0" aria-hidden="true" data-id="element-2616" data-mp="1mbb6rgzoblwl">
        <ArrowRightIcon className="w-4 h-4 text-[#4F46E5]" strokeWidth={2} data-id="element-2617" data-mp="1v5ptu5qg40q9" />
      </span>
    </div>;
}
export function FloatingNotification({
  isFR
}: P) {
  const pick = pk(isFR);
  return <div className={`${CARD} w-[230px] px-3 py-2.5 flex items-start gap-2.5`} data-id="element-2618" data-mp="253ngiosnppgr">
      <span className="w-7 h-7 rounded-lg bg-[#1B1E3A] flex items-center justify-center shrink-0" aria-hidden="true" data-id="element-2619" data-mp="251klfqcnd656">
        <span className="grid grid-cols-2 gap-[2px]" data-id="element-2620" data-mp="apmbfm6ik688">
          {[0, 1, 2, 3].map(i => <span key={i} className="w-[4px] h-[4px] rounded-[1px] bg-white" data-id="element-2621" data-mp="2mczi4g0gmy33" />)}
        </span>
      </span>
      <span className="min-w-0 flex-1" data-id="element-2622" data-mp="2rckhgrjjl8gc">
        <span className="flex items-center justify-between font-sans text-[10px] text-[#6B7180]" data-id="element-2623" data-mp="2rbj6en2j0lym">
          <span className="font-semibold text-[#121212]" data-id="element-2624" data-mp="2cmn0avk15j0n">{previewCopy.notifApp}</span>
          {pick(previewCopy.notifNow)}
        </span>
        <span className="block font-sans text-[10.5px] font-semibold text-[#121212] mt-0.5" data-id="element-2625" data-mp="23mhs4j70iglf">{pick(previewCopy.notifTitle)}</span>
        <span className="block font-sans text-[10px] text-[#3A4050] leading-snug" data-id="element-2626" data-mp="27obqgktqj8if">{pick(previewCopy.notifBody)}</span>
      </span>
    </div>;
}
const CENTER: [number, number] = [50, 44];
export function CityNetwork({
  isFR,
  className = ''
}: P & {
  className?: string;
}) {
  const pick = pk(isFR);
  return <div className={`relative ${className}`} data-id="element-2627" data-mp="3o3cz3tm4j8k8">
      <img src={CTA_CITY_MAP} alt="" className="absolute inset-0 w-full h-full object-cover" style={{
      WebkitMaskImage: 'radial-gradient(ellipse 70% 70% at 55% 45%, #000 45%, transparent 100%)',
      maskImage: 'radial-gradient(ellipse 70% 70% at 55% 45%, #000 45%, transparent 100%)'
    }} data-id="element-2628" data-mp="3o0ifg4axprrv" />
      <div className="absolute rounded-full bg-[#6D5DF6]/15 blur-2xl" style={{
      left: `${CENTER[0]}%`,
      top: `${CENTER[1]}%`,
      width: 260,
      height: 260,
      transform: 'translate(-50%,-50%)'
    }} aria-hidden="true" data-id="element-2629" data-mp="3hn4gkym1fdvp" />
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true" data-id="element-2630" data-mp="e7fhcc0fv15x">
        {citizenNodes.map(([x, y], i) => <line key={i} x1={CENTER[0]} y1={CENTER[1]} x2={x} y2={y} stroke="#5B4BE0" strokeWidth="1.2" strokeDasharray="3 4" vectorEffect="non-scaling-stroke" opacity="0.7" data-id="element-2631" data-mp="4e8f300zw1a2" data-mpi={i} />)}
      </svg>
      {citizenNodes.map(([x, y], i) => <span key={i} className="absolute w-10 h-10 rounded-full bg-white shadow-[0_8px_18px_-8px_rgba(40,36,120,0.45)] flex items-center justify-center" style={{
      left: `${x}%`,
      top: `${y}%`,
      transform: 'translate(-50%,-50%)'
    }} aria-hidden="true" data-id="element-2632" data-mp="10e9t1wxzwv1u" data-mpi={i}>
          <UserIcon className="w-4 h-4 text-[#4F46E5] fill-[#4F46E5]" data-id="element-2633" data-mp="1ee2fvh6eir4j" />
        </span>)}
      <div className="absolute flex items-center" style={{
      left: `${CENTER[0]}%`,
      top: `${CENTER[1]}%`,
      transform: 'translate(-28px,-50%)'
    }} data-id="element-2634" data-mp="1jcvsz8hs0g46">
        <span className="relative z-10 w-14 h-14 rounded-full bg-[#4F46E5] ring-[10px] ring-[#4F46E5]/15 flex items-center justify-center shadow-[0_10px_24px_-8px_rgba(79,70,229,0.7)]" data-id="element-2635" data-mp="1nco700pxvegx">
          <MapPinIcon className="w-6 h-6 text-white" strokeWidth={2} aria-hidden="true" data-id="element-2636" data-mp="1x2ze1w4x63s1" />
        </span>
        <span className="-ml-3 pl-6 pr-4 py-2 rounded-full bg-white font-sans text-[15px] font-semibold text-[#121A3A] shadow-[0_8px_20px_-10px_rgba(20,20,60,0.5)] whitespace-nowrap" data-id="element-2637" data-mp="224evle7w1s5o">
          {pick(networkCopy.city)}
        </span>
      </div>
    </div>;
}
function LogoCrop({
  logo,
  label,
  height
}: {
  logo: typeof LOGO_BCITI;
  label: string;
  height: number;
}) {
  const W = 1536;
  const H = 1024;
  return <span role="img" aria-label={label} className="block mix-blend-multiply" style={{
    height,
    aspectRatio: `${logo.w} / ${logo.h}`,
    backgroundImage: `url("${logo.src}")`,
    backgroundSize: `${W / logo.w * 100}% auto`,
    backgroundPosition: `${logo.x / (W - logo.w) * 100}% ${logo.y / (H - logo.h) * 100}%`,
    backgroundRepeat: 'no-repeat'
  }} data-id="element-2638" data-mp="31dbhvkr90um4" />;
}
export function BrandSignature({
  isFR
}: P) {
  return <div className="flex items-center justify-center gap-5 sm:gap-7" data-id="element-2639" data-mp="2dnh6z8qhpg4i">
      <span className="hidden md:block w-40 h-px bg-[#DCDDF0]" aria-hidden="true" data-id="element-2640" data-mp="1py6f6gr4aw2i" />
      <div className="flex flex-wrap items-center justify-center gap-x-5 sm:gap-x-7 gap-y-3" data-id="element-2641" data-mp="1kzv11ttshzeh">
        <LogoCrop logo={LOGO_BCITI} label="BCITI" height={30} data-id="element-2642" data-mp="hsxw1zprus7" />
        <span className="w-px h-8 bg-[#DCDDF0]" aria-hidden="true" data-id="element-2643" data-mp="g9i0chyzlqy" />
        <LogoCrop logo={LOGO_ATNA} label="ATNA" height={34} data-id="element-2644" data-mp="59axalm39nb2" />
        <span className="hidden sm:block w-px h-8 bg-[#DCDDF0]" aria-hidden="true" data-id="element-2645" data-mp="3nm8pg6cn3buo" />
        <span className="font-serif text-[22px] sm:text-[26px] text-[#121A3A] sm:whitespace-nowrap text-center" data-id="element-2646" data-mp="3nkfliytev5qd">{isFR ? ctaCopy.tagline.fr : ctaCopy.tagline.en}</span>
      </div>
      <span className="hidden md:block w-40 h-px bg-[#DCDDF0]" aria-hidden="true" data-id="element-2647" data-mp="k6bf9xeha3wc" />
    </div>;
}